<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { $, defineSchema } from '@bagelink/vue'
const schema = defineSchema({
	eyebrow: $.text('Eyebrow'),
	title: $.text('Heading', { default: 'Heading' }),
	headingFont: $.select('Font', [
		{ label: 'Special project font', value: 'special' },
		{ label: 'Regular document font', value: 'document' },
	], { default: 'special' }).if('title pr'),
	level: $.select('Heading level', [
		{ label: 'Page heading (H1)', value: 'h1' },
		{ label: 'Section heading (H2)', value: 'h2' },
		{ label: 'Subsection heading (H3)', value: 'h3' },
	], { default: 'h2' }),
	size: $.select('Size', [
		{ label: 'Small', value: 'small' },
		{ label: 'Medium', value: 'medium' },
		{ label: 'Large', value: 'large' },
		{ label: 'Extra large', value: 'extra-large' },
	], { default: 'medium' }),
	bold: $.checkbox('Bold', { default: false }),
	align: $.select('Alignment', [
		{ label: 'Start', value: 'start' },
		{ label: 'Center', value: 'center' },
		{ label: 'End', value: 'end' },
	], { default: 'center' }),
	width: $.select('Content width', [
		{ label: 'Narrow (400px)', value: 'narrow' },
		{ label: 'Regular (700px)', value: 'regular' },
		{ label: 'Wide (1100px)', value: 'wide' },
		{ label: 'Full width', value: 'full' },
	], { default: 'regular' }),
	surface: $.select('Background', [
		{ label: 'Page background', value: 'page' },
		{ label: 'White surface', value: 'surface' },
		{ label: 'Brand color', value: 'brand' },
		{ label: 'Dark', value: 'dark' },
		{ label: 'Image', value: 'image' },
	], { default: 'page' }),
	backgroundImage: $.upload('Background image').if('surface eq "image"'),
	backgroundFit: $.select('Background image fit', [
		{ label: 'Cover', value: 'cover' },
		{ label: 'Contain', value: 'contain' },
		{ label: 'Fill', value: 'fill' },
	], { default: 'cover' }).if('surface eq "image" and backgroundImage pr'),
	dimBackground: $.checkbox('Dim background', { default: false }).if('surface eq "image" and backgroundImage pr'),
	compact: $.checkbox('Compact spacing', { default: false }),
})
export const Heading = defineBlock({
	name: 'Heading',
	label: 'Heading',
	icon: 'title',
	thumbnail: '/block-thumbs/Heading.svg',
	category: 'General',
	order: 108,
	schema,
})
</script>
<script setup lang="ts">
import { computed } from 'vue'

import { pathKeyToURL } from '@bagelink/vue'
const props = defineProps<{
	eyebrow?: string
	title?: string
	headingFont?: 'special' | 'document'
	level?: 'h1' | 'h2' | 'h3'
	size?: 'small' | 'medium' | 'large' | 'extra-large'
	bold?: boolean
	align?: 'start' | 'center' | 'end'
	width?: 'narrow' | 'regular' | 'wide' | 'full'
	surface?: 'page' | 'surface' | 'brand' | 'dark' | 'image'
	backgroundImage?: string
	backgroundFit?: 'cover' | 'contain' | 'fill'
	dimBackground?: boolean
	compact?: boolean
}>()

const surfaceClass = computed(() => ({
	page: 'bg-bg',
	surface: 'bg-box',
	brand: 'bg-primary color-white keep-white',
	dark: 'bg-black color-white keep-white',
	image: 'color-white keep-white',
}[props.surface ?? 'page']))
const sectionStyle = computed(() => props.surface === 'image' && props.backgroundImage
	? {
			backgroundImage: `${props.dimBackground ? 'linear-gradient(#0006, #0006), ' : ''}url("${pathKeyToURL(props.backgroundImage) || props.backgroundImage}")`,
			backgroundPosition: 'center',
			backgroundRepeat: 'no-repeat',
			backgroundSize: props.backgroundFit === 'fill' ? '100% 100%' : (props.backgroundFit ?? 'cover'),
		}
	: undefined)
const widthClass = computed(() => ({
	narrow: 'w-400px',
	regular: 'w-700px',
	wide: 'w-1100px',
	full: 'w-100p',
}[props.width ?? 'regular']))
const sizeClass = computed(() => ({
	small: 'txt-28 m_txt-24 line-height-12 m_line-height-13',
	medium: 'txt-48 m_txt-32 line-height-11 m_line-height-12',
	large: 'txt-64 m_txt-40 line-height-1 m_line-height-11',
	'extra-large': 'txt-70 m_txt-48 line-height-1 m_line-height-11',
}[props.size ?? 'medium']))
</script>

<template>
	<section
		class="px-2 m_px-1"
		:class="[surfaceClass, compact ? 'py-1' : 'py-2 m_py-1']"
		:style="sectionStyle"
	>
		<div
			:class="[
				widthClass,
				align === 'start' ? 'txt-start' : align === 'end' ? 'txt-end' : 'txt-center',
			]"
		>
			<p v-if="eyebrow" class="m-0 pb-05 uppercase tracking-wide semi">{{ eyebrow }}</p>
			<component
				:is="level || 'h2'"
				v-if="title"
				class="m-0 balance"
				:class="[
					sizeClass,
					{
						'blox-special-heading': headingFont !== 'document',
						bold,
					},
				]"
			>
				{{ title }}
			</component>
		</div>
	</section>
</template>
